/**
 * TanStack Query hooks over the planning client.
 *
 * House rules (STYLEGUIDE §6): no optimistic UI — a mutation swaps the server's
 * document into the cache and the UI re-renders from it; the document's
 * `version` from the SAME response is the next CAS token. The ONE sanctioned
 * exception is `useSetPositions` (F-25): a drop must move the row instantly,
 * so the reordered document goes into the cache optimistically and is replaced
 * by the server's answer (or rolled back) a moment later. The twins below are
 * the same exception for the other manual queues: team roster, a person's own
 * queue, and the GLOBAL order of «Все проекты» (`useSetAllPositions`, F-28).
 *
 * Concurrency (arch §3.3): a `409` answer carries the CURRENT document in
 * `body.current`. The hook puts it into the cache immediately, so
 *  - every open view re-renders on fresh data (no refetch),
 *  - an open edit modal that re-resolves its entity by id (Projects/Streams
 *    pages do) re-opens on the fresh state — and shows a small non-blocking
 *    «Изменён кем-то другим — данные обновлены» notice instead of an error.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from './client';
import type {
  AllPositionsBody,
  AuditQuery,
  CreateCategoryBody,
  CreatePersonBody,
  CreateProjectBody,
  CreateStatusBody,
  CreateStreamBody,
  CreateSystemBody,
  CreateTeamBody,
  DeleteBody,
  DictionaryPositionsBody,
  PatchCategoryBody,
  PatchPersonBody,
  PatchProjectBody,
  PatchStatusBody,
  PatchStreamBody,
  PatchSystemBody,
  PatchTeamBody,
  PersonPositionsBody,
  PositionsBody,
  TeamPositionsBody,
} from './planning';
import {
  createCategory,
  createPerson,
  createProject,
  createStatus,
  createStream,
  createSystem,
  createTeam,
  deleteCategory,
  deletePerson,
  deleteProject,
  deleteStatus,
  deleteStream,
  deleteSystem,
  deleteTeam,
  getAudit,
  getRegistry,
  patchCategory,
  patchPerson,
  patchProject,
  patchStatus,
  patchStream,
  patchSystem,
  patchTeam,
  setAllPositions,
  setDictionaryPositions,
  setPersonPositions,
  setPositions,
  setTeamPositions,
  QUERY_KEYS,
} from './planning';
import type { RegistryDocument } from './types';
import { withAllPositions, withPersonPositions, withPositions, withTeamPositions } from '../lib/manual-order';

export function useRegistry() {
  return useQuery({
    queryKey: QUERY_KEYS.registry,
    queryFn: ({ signal }) => getRegistry(signal),
    // The registry is small (80 projects) and the UI edits it constantly.
    staleTime: 10_000,
    // Keep the previous document visible while refetching after a mutation.
    placeholderData: (previous) => previous,
  });
}

export function useAudit(query: AuditQuery) {
  return useQuery({
    queryKey: [...QUERY_KEYS.audit, query] as const,
    queryFn: ({ signal }) => getAudit(query, signal),
    placeholderData: (previous) => previous,
    retry: 1,
  });
}

/** Every mutation returns the whole registry document — swap it into the cache. */
function useRegistryMutation<TInput>(mutate: (input: TInput) => Promise<RegistryDocument>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mutate,
    onSuccess: (document) => {
      queryClient.setQueryData(QUERY_KEYS.registry, document);
      // Audit grows with every mutation; refetch whatever any view holds.
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
    onError: (error) => {
      // 409: heal the cache from `body.current` — the next attempt (or the
      // re-opened modal) works from the version that is actually stored.
      if (isConflict(error)) {
        queryClient.setQueryData(QUERY_KEYS.registry, (error as ApiError).currentDocument as RegistryDocument);
        void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
      }
    },
  });
}

/** A 409 whose body carries the fresh document to recover with. */
export function isConflict(error: unknown): error is ApiError {
  return error instanceof ApiError && error.isConflict;
}

/** Field-level issues of a 400/422, for per-field rendering in a form. */
export function fieldIssues(error: unknown): Map<string, string> {
  const map = new Map<string, string>();
  if (!(error instanceof ApiError)) return map;
  for (const issue of error.issues) {
    if (!map.has(issue.field)) map.set(issue.field, issue.message);
  }
  return map;
}

/**
 * The human part of an API error for dialog/banner rendering: the parsed
 * body's `message` — the domain wording («team … still has 4 people — move
 * them to another team first») — not `ApiError.message`, which leads with the
 * transport prefix (`DELETE /registry/teams/x failed with HTTP 422: `) and
 * pushes the readable part to the tail. Non-API errors pass their message
 * through; `null` = nothing to show.
 */
export function errorMessage(error: unknown): string | null {
  if (!(error instanceof ApiError)) {
    return error instanceof Error && error.message ? error.message : null;
  }
  const message = error.body?.message;
  if (typeof message === 'string' && message !== '') return message;
  if (Array.isArray(message) && message.length > 0) return message.join('; ');
  return error.message;
}

export const useCreateProject = () => useRegistryMutation((body: CreateProjectBody) => createProject(body));
export const usePatchProject = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchProjectBody }) => patchProject(id, body));
export const useDeleteProject = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deleteProject(id, version));

/**
 * F-25 — the one optimistic mutation: on drop the reordered document is shown
 * immediately, then the server's answer replaces it. A 409 heals from
 * `body.current` (same machinery as every other mutation); any other failure
 * rolls the cache back to the pre-drop document. The variants below
 * (`useSetTeamPositions` / `useSetPersonPositions` / `useSetAllPositions`) are
 * its twins for the other manual queues — team roster, a person's own queue,
 * and the global «Все проекты» order (F-28).
 */
export function useSetPositions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PositionsBody) => setPositions(body),
    onMutate: async ({ streamId, orderedIds }: PositionsBody) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.registry });
      const previous = queryClient.getQueryData<RegistryDocument>(QUERY_KEYS.registry);
      if (previous) queryClient.setQueryData(QUERY_KEYS.registry, withPositions(previous, streamId, orderedIds));
      return { previous };
    },
    onSuccess: (document) => {
      queryClient.setQueryData(QUERY_KEYS.registry, document);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
    onError: (error, _body, context) => {
      if (isConflict(error)) {
        queryClient.setQueryData(
          QUERY_KEYS.registry,
          (error as ApiError).currentDocument as RegistryDocument,
        );
      } else if (context?.previous) {
        queryClient.setQueryData(QUERY_KEYS.registry, context.previous);
      }
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
  });
}

/**
 * The same optimistic mutation for the TEAM roster's manual order: the drop
 * shows `teamPosition` stamped on the team's assignments right away, the
 * server's answer (or the 409 heal / rollback) replaces it a moment later.
 */
export function useSetTeamPositions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: TeamPositionsBody) => setTeamPositions(body),
    onMutate: async ({ teamId, orderedProjectIds }: TeamPositionsBody) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.registry });
      const previous = queryClient.getQueryData<RegistryDocument>(QUERY_KEYS.registry);
      if (previous) queryClient.setQueryData(QUERY_KEYS.registry, withTeamPositions(previous, teamId, orderedProjectIds));
      return { previous };
    },
    onSuccess: (document) => {
      queryClient.setQueryData(QUERY_KEYS.registry, document);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
    onError: (error, _body, context) => {
      if (isConflict(error)) {
        queryClient.setQueryData(QUERY_KEYS.registry, (error as ApiError).currentDocument as RegistryDocument);
      } else if (context?.previous) {
        queryClient.setQueryData(QUERY_KEYS.registry, context.previous);
      }
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
  });
}

/** The same optimistic mutation for ONE person's own queue (`personPosition`). */
export function useSetPersonPositions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PersonPositionsBody) => setPersonPositions(body),
    onMutate: async ({ personId, orderedProjectIds }: PersonPositionsBody) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.registry });
      const previous = queryClient.getQueryData<RegistryDocument>(QUERY_KEYS.registry);
      if (previous) queryClient.setQueryData(QUERY_KEYS.registry, withPersonPositions(previous, personId, orderedProjectIds));
      return { previous };
    },
    onSuccess: (document) => {
      queryClient.setQueryData(QUERY_KEYS.registry, document);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
    onError: (error, _body, context) => {
      if (isConflict(error)) {
        queryClient.setQueryData(QUERY_KEYS.registry, (error as ApiError).currentDocument as RegistryDocument);
      } else if (context?.previous) {
        queryClient.setQueryData(QUERY_KEYS.registry, context.previous);
      }
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
  });
}

/**
 * The same optimistic mutation for the GLOBAL order of «Все проекты» (F-28):
 * a drop or the row's «наверх» shows the reordered list (and the new «№»
 * numbers) at once, the server's answer — or the 409 heal / rollback —
 * replaces it a moment later. The body carries the FULL registry order, so
 * the optimistic image is scope-free, like the endpoint itself.
 */
export function useSetAllPositions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: AllPositionsBody) => setAllPositions(body),
    onMutate: async ({ orderedIds }: AllPositionsBody) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEYS.registry });
      const previous = queryClient.getQueryData<RegistryDocument>(QUERY_KEYS.registry);
      if (previous) queryClient.setQueryData(QUERY_KEYS.registry, withAllPositions(previous, orderedIds));
      return { previous };
    },
    onSuccess: (document) => {
      queryClient.setQueryData(QUERY_KEYS.registry, document);
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
    onError: (error, _body, context) => {
      if (isConflict(error)) {
        queryClient.setQueryData(QUERY_KEYS.registry, (error as ApiError).currentDocument as RegistryDocument);
      } else if (context?.previous) {
        queryClient.setQueryData(QUERY_KEYS.registry, context.previous);
      }
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.audit });
    },
  });
}

export const useCreatePerson = () => useRegistryMutation((body: CreatePersonBody) => createPerson(body));
export const usePatchPerson = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchPersonBody }) => patchPerson(id, body));
export const useDeletePerson = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deletePerson(id, version));

export const useCreateTeam = () => useRegistryMutation((body: CreateTeamBody) => createTeam(body));
export const usePatchTeam = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchTeamBody }) => patchTeam(id, body));
export const useDeleteTeam = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deleteTeam(id, version));

export const useCreateStream = () => useRegistryMutation((body: CreateStreamBody) => createStream(body));
export const usePatchStream = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchStreamBody }) => patchStream(id, body));
export const useDeleteStream = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deleteStream(id, version));

export const useCreateCategory = () => useRegistryMutation((body: CreateCategoryBody) => createCategory(body));
export const usePatchCategory = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchCategoryBody }) => patchCategory(id, body));
export const useDeleteCategory = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deleteCategory(id, version));

/** The managed status dictionary (F-01/F-23): create / rename / delete. */
export const useCreateStatus = () => useRegistryMutation((body: CreateStatusBody) => createStatus(body));
export const usePatchStatus = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchStatusBody }) => patchStatus(id, body));
export const useDeleteStatus = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deleteStatus(id, version));

/** The full new order of one dictionary (statuses/streams/categories). */
export const useSetDictionaryPositions = () =>
  useRegistryMutation((body: DictionaryPositionsBody) => setDictionaryPositions(body));

// -- Systems (F-21, managed since 2026-10-14) --------------------------------

export const useCreateSystem = () => useRegistryMutation((body: CreateSystemBody) => createSystem(body));
export const usePatchSystem = () =>
  useRegistryMutation(({ id, body }: { id: string; body: PatchSystemBody }) => patchSystem(id, body));
/** 422 `invalid_ref` (the linked-project count) while projects still link the system. */
export const useDeleteSystem = () =>
  useRegistryMutation(({ id, version }: DeleteBody & { id: string }) => deleteSystem(id, version));
